import { useState, useEffect, useMemo, useCallback } from "react";
import type { BacklogItem } from "@/hooks/useBacklogItems";
import type { DateRangeFilter, DropdownOption, SortColumn, SortDirection, SortRule, UserData } from "@/types/backlog";
import { parsePgArray } from "@/lib/pgArray";
import { inScope } from "@/lib/pageTree";
import { matchesPipelineFilter } from "@/lib/pipelines";
import {
  compareFieldValues,
  fieldIdOfSortColumn,
  isEmptyValue,
  isFieldFilterSet,
  matchesFieldFilter,
  parseFieldFilters,
  searchableFieldText,
  type CustomField,
  type FieldFilter,
} from "@/lib/customFields";

const FILTERS_STORAGE_KEY = "backlog-filters";

const EMPTY_RANGE: DateRangeFilter = { from: null, to: null };

const isRangeSet = (range: DateRangeFilter) => range.from !== null || range.to !== null;

/**
 * Whether a timestamp falls inside an inclusive day range.
 *
 * The bounds are bare local dates, so they are widened to the whole day here —
 * a "to" of the 9th has to include something filed at 4pm on the 9th, which
 * comparing against midnight would drop.
 */
const withinRange = (iso: string | null, range: DateRangeFilter) => {
  if (!isRangeSet(range)) return true;
  if (!iso) return false;
  const at = new Date(iso).getTime();
  if (range.from && at < new Date(`${range.from}T00:00:00`).getTime()) return false;
  if (range.to && at > new Date(`${range.to}T23:59:59.999`).getTime()) return false;
  return true;
};

/** A stored sort stack, re-validated — localStorage is not a typed channel. */
const parseSortStack = (raw: unknown): SortRule[] => {
  if (!Array.isArray(raw)) return [];
  // Also the migration path: a stack stored while Task/Assigned/Status were
  // sortable drops those rules on read rather than sorting by a column that no
  // longer offers the control to undo it.
  const columns: SortColumn[] = ["priority", "created", "updated"];
  const seen = new Set<SortColumn>();
  const out: SortRule[] = [];
  for (const entry of raw) {
    const column = entry?.column as SortColumn;
    const direction = entry?.direction as SortDirection;
    // A custom field's sort (#9399) is kept by shape; one whose field has
    // since been deleted simply compares equal everywhere.
    const known = columns.includes(column) || (typeof column === "string" && fieldIdOfSortColumn(column) !== null);
    if (!known || (direction !== "asc" && direction !== "desc")) continue;
    if (seen.has(column)) continue;
    seen.add(column);
    out.push({ column, direction });
  }
  return out;
};

const parseRange = (raw: unknown): DateRangeFilter => {
  const from = (raw as DateRangeFilter)?.from;
  const to = (raw as DateRangeFilter)?.to;
  return { from: typeof from === "string" ? from : null, to: typeof to === "string" ? to : null };
};

const NO_FIELDS: readonly CustomField[] = [];

interface FilterDropdownData {
  profiles: UserData[];
  priorities: DropdownOption[];
  statuses?: DropdownOption[];
  allOptions?: DropdownOption[];
}

interface UseBacklogFiltersOptions {
  items: BacklogItem[];
  dropdownData: FilterDropdownData | undefined;
  /**
   * The departments the current view covers: the selected one and everything
   * nested beneath it (#9304). Null shows every ticket.
   */
  subsheetScope?: ReadonlySet<string> | null;
  /** The tenant's custom fields (#9399), for their filters, sorts and search. */
  customFields?: readonly CustomField[];
  /**
   * Which of them apply to a ticket's departments. A value whose field doesn't
   * apply is hidden (decision 4), so it neither matches nor sorts. Omitted,
   * every field counts.
   */
  fieldsFor?: (subsheetIds: readonly string[]) => readonly CustomField[];
  externalPriorityFilter?: string | null;
  externalStatusFilter?: string | null;
  externalAssigneeFilter?: string | null;
}

export const useBacklogFilters = ({
  items,
  dropdownData,
  subsheetScope,
  customFields = NO_FIELDS,
  fieldsFor,
  externalPriorityFilter,
  externalStatusFilter,
  externalAssigneeFilter,
}: UseBacklogFiltersOptions) => {
  // Read initial values from URL params or localStorage (lazy — only on first render)
  const readInitialFilters = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const storedFilters = (() => {
      try {
        const stored = localStorage.getItem(FILTERS_STORAGE_KEY);
        return stored ? JSON.parse(stored) : null;
      } catch {
        return null;
      }
    })();
    return { urlParams, storedFilters };
  };

  // Filter state — lazy initializers avoid re-parsing URL/localStorage on every render
  const [searchQuery, setSearchQuery] = useState(() => {
    const { urlParams, storedFilters } = readInitialFilters();
    return urlParams.get("search") || storedFilters?.searchQuery || "";
  });
  const [selectedAssignees, setSelectedAssignees] = useState<string[]>(() => {
    const { urlParams, storedFilters } = readInitialFilters();
    return urlParams.get("assignees")?.split(",") ?? storedFilters?.selectedAssignees ?? [];
  });
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>(() => {
    const { urlParams, storedFilters } = readInitialFilters();
    return urlParams.get("statuses")?.split(",") ?? storedFilters?.selectedStatuses ?? [];
  });
  const [selectedPriorities, setSelectedPriorities] = useState<string[]>(() => {
    const { urlParams, storedFilters } = readInitialFilters();
    return urlParams.get("priorities")?.split(",") ?? storedFilters?.selectedPriorities ?? [];
  });
  const [selectedAddedBys, setSelectedAddedBys] = useState<string[]>(() => {
    const { urlParams, storedFilters } = readInitialFilters();
    return urlParams.get("addedBys")?.split(",") ?? storedFilters?.selectedAddedBys ?? [];
  });
  const [selectedTicketTypes, setSelectedTicketTypes] = useState<string[]>(() => {
    const { urlParams, storedFilters } = readInitialFilters();
    return urlParams.get("ticketTypes")?.split(",") ?? storedFilters?.selectedTicketTypes ?? [];
  });
  // Pipelines (#9304), including NO_PIPELINE for tickets that have none.
  const [selectedPipelines, setSelectedPipelines] = useState<string[]>(() => {
    const { urlParams, storedFilters } = readInitialFilters();
    return urlParams.get("pipelines")?.split(",") ?? storedFilters?.selectedPipelines ?? [];
  });
  // "team" reads from the URL as well as storage so TeamDetail's "View queue"
  // button can deep-link straight into a team's work.
  const [selectedTeams, setSelectedTeams] = useState<string[]>(() => {
    const { urlParams, storedFilters } = readInitialFilters();
    return (
      urlParams.get("team")?.split(",") ?? urlParams.get("teams")?.split(",") ?? storedFilters?.selectedTeams ?? []
    );
  });
  const [createdRange, setCreatedRange] = useState<DateRangeFilter>(() => {
    const { storedFilters } = readInitialFilters();
    return parseRange(storedFilters?.createdRange);
  });
  const [updatedRange, setUpdatedRange] = useState<DateRangeFilter>(() => {
    const { storedFilters } = readInitialFilters();
    return parseRange(storedFilters?.updatedRange);
  });
  /**
   * The sort stack, in the order the user built it. Empty is the normal state
   * and means "the default order" (status → priority → assignee, below) — a
   * fallback nothing in the UI names, because a reader who has not sorted
   * anything has not asked a question that needs answering.
   */
  /** Custom field filters (#9399), by field id. */
  const [fieldFilters, setFieldFilters] = useState<Record<string, FieldFilter>>(() => {
    const { storedFilters } = readInitialFilters();
    return parseFieldFilters(storedFilters?.fieldFilters);
  });
  const setFieldFilter = useCallback((fieldId: string, filter: FieldFilter | undefined) => {
    setFieldFilters((prev) => {
      const next = { ...prev };
      if (isFieldFilterSet(filter)) next[fieldId] = filter!;
      else delete next[fieldId];
      return next;
    });
  }, []);
  const [sortStack, setSortStack] = useState<SortRule[]>(() => {
    const { storedFilters } = readInitialFilters();
    return parseSortStack(storedFilters?.sortStack);
  });
  // Apply external filters from statistics charts
  useEffect(() => {
    if (externalPriorityFilter) setSelectedPriorities([externalPriorityFilter]);
  }, [externalPriorityFilter]);

  useEffect(() => {
    if (externalStatusFilter) setSelectedStatuses([externalStatusFilter]);
  }, [externalStatusFilter]);

  useEffect(() => {
    if (externalAssigneeFilter) setSelectedAssignees([externalAssigneeFilter]);
  }, [externalAssigneeFilter]);

  // Persist filters to localStorage
  useEffect(() => {
    const filters = {
      searchQuery,
      selectedAssignees,
      selectedStatuses,
      selectedPriorities,
      selectedAddedBys,
      selectedTicketTypes,
      selectedPipelines,
      selectedTeams,
      createdRange,
      updatedRange,
      fieldFilters,
      sortStack,
    };
    try {
      localStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify(filters));
    } catch (error) {
      console.error("Failed to save filters to localStorage:", error);
    }
  }, [
    searchQuery,
    selectedAssignees,
    selectedStatuses,
    selectedPriorities,
    selectedAddedBys,
    selectedTicketTypes,
    selectedPipelines,
    selectedTeams,
    createdRange,
    updatedRange,
    fieldFilters,
    sortStack,
  ]);

  /**
   * Apply a direction to a column, which is the ONLY way the stack changes.
   *
   * Picking the direction a column already has removes it — the same click
   * that turned the sort on turns it off, so nothing can end up stuck in the
   * stack with no way to see how it got there. Picking the other direction
   * flips it *in place*, keeping its number: re-reading the same column the
   * other way round is not a new question, and re-stacking it to the bottom
   * would silently re-order every other column with it.
   */
  const toggleSort = useCallback((column: SortColumn, direction: SortDirection) => {
    setSortStack((prev) => {
      const existing = prev.find((rule) => rule.column === column);
      if (!existing) return [...prev, { column, direction }];
      if (existing.direction === direction) return prev.filter((rule) => rule.column !== column);
      return prev.map((rule) => (rule.column === column ? { column, direction } : rule));
    });
  }, []);

  const removeSort = useCallback((column: SortColumn) => {
    setSortStack((prev) => prev.filter((rule) => rule.column !== column));
  }, []);

  const clearSort = useCallback(() => setSortStack([]), []);

  // Apply filters, search, and sort
  // Each ticket's custom field text, lowercased, built when the tickets or the
  // fields change rather than on every keystroke of a search (#9399).
  // A ticket's value for a field, or undefined when the field doesn't apply.
  const fieldValue = useCallback(
    (item: BacklogItem, field: CustomField): unknown =>
      !fieldsFor || fieldsFor(item.subsheet_ids ?? []).includes(field) ? item.custom_fields[field.id] : undefined,
    [fieldsFor],
  );

  const fieldSearchText = useMemo(() => {
    const out = new Map<string, string>();
    if (customFields.length === 0) return out;
    for (const item of items) {
      const text = searchableFieldText(
        fieldsFor ? fieldsFor(item.subsheet_ids ?? []) : customFields,
        item.custom_fields,
      );
      if (text) out.set(item.id, text.toLowerCase());
    }
    return out;
  }, [items, customFields, fieldsFor]);

  const filteredItems = useMemo(() => {
    let filtered = [...items];

    // Subsheet filter
    if (subsheetScope) {
      filtered = filtered.filter((item) => inScope(item.subsheet_ids, subsheetScope));
    }

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase().trim();

      // Check if searching by ticket number (e.g., "#123" or "123")
      const ticketQuery = query.startsWith("#") ? query.slice(1) : query;
      const isTicketSearch = /^\d+$/.test(ticketQuery);

      filtered = filtered.filter((item) => {
        // Ticket number match (exact or prefix)
        if (isTicketSearch) {
          const ticketStr = String(item.ticket_number);
          if (ticketStr === ticketQuery || ticketStr.startsWith(ticketQuery)) return true;
        }

        // Text matches
        if (item.task?.toLowerCase().includes(query)) return true;
        if (item.description?.toLowerCase().includes(query)) return true;
        if (item.assigned_to_id && item.assigned_to_id.length > 0) {
          for (const assigneeId of item.assigned_to_id) {
            const assignee = dropdownData?.profiles.find((p) => p.id === assigneeId);
            if (assignee) {
              const name = `${assignee.first_name || ""} ${assignee.last_name || ""} ${assignee.email}`.toLowerCase();
              if (name.includes(query)) return true;
            }
          }
        }
        const addedBy = dropdownData?.profiles.find((p) => p.id === item.added_by_id);
        if (addedBy) {
          const name = `${addedBy.first_name || ""} ${addedBy.last_name || ""} ${addedBy.email}`.toLowerCase();
          if (name.includes(query)) return true;
        }
        if (fieldSearchText.get(item.id)?.includes(query)) return true;
        return false;
      });
    }

    // Assigned To filter — people, and only people. It used to carry an
    // "Unassigned" sentinel that matched an empty assignee list; an unassigned
    // ticket is not a state this workspace has, so the facet has gone along
    // with the option row that offered it.
    if (selectedAssignees.length > 0) {
      filtered = filtered.filter((item) => (item.assigned_to_id ?? []).some((id) => selectedAssignees.includes(id)));
    }

    // Status filter
    if (selectedStatuses.length > 0) {
      filtered = filtered.filter((item) => item.status_id && selectedStatuses.includes(item.status_id));
    }

    // Priority filter
    if (selectedPriorities.length > 0) {
      filtered = filtered.filter((item) => item.priority_id && selectedPriorities.includes(item.priority_id));
    }

    // Added By filter
    if (selectedAddedBys.length > 0) {
      filtered = filtered.filter((item) => item.added_by_id && selectedAddedBys.includes(item.added_by_id));
    }

    // Ticket Type filter
    if (selectedPipelines.length > 0) {
      filtered = filtered.filter((item) => matchesPipelineFilter(item.pipeline_id ?? null, selectedPipelines));
    }

    if (selectedTicketTypes.length > 0) {
      filtered = filtered.filter((item) => item.ticket_type_id && selectedTicketTypes.includes(item.ticket_type_id));
    }

    // Team filter — matches EITHER a tag or the owning queue. One control for
    // both because "show me Billing's work" is one question, and splitting it
    // into two facets would make the common case take two clicks and the
    // subtle case (tagged but owned elsewhere) easy to miss.
    if (selectedTeams.length > 0) {
      filtered = filtered.filter((item) => {
        if (item.assigned_team_id && selectedTeams.includes(item.assigned_team_id)) return true;
        return parsePgArray(item.team_ids).some((id) => selectedTeams.includes(id));
      });
    }

    // Date-range filters, set from the Created / Last updated header dropdowns.
    // These replaced the old "active in the last N hours" filter, which asked
    // the same recency question of the activity log and had no column of its
    // own to be read under.
    if (isRangeSet(createdRange)) {
      filtered = filtered.filter((item) => withinRange(item.created_at, createdRange));
    }
    if (isRangeSet(updatedRange)) {
      filtered = filtered.filter((item) => withinRange(item.updated_at, updatedRange));
    }

    // Custom field filters (#9399). One whose field has been deleted is ignored.
    for (const [fieldId, filter] of Object.entries(fieldFilters)) {
      const field = customFields.find((f) => f.id === fieldId);
      if (!field) continue;
      filtered = filtered.filter((item) => matchesFieldFilter(field, fieldValue(item, field), filter));
    }

    // Workflow position for a status, rank for a priority. On-screen sort uses
    // sort_index (active-work-first) when set, falling back to order_index;
    // dropdowns/pickers keep using order_index elsewhere.
    const statusOrderMap = new Map<string, number>();
    const priorityOrderMap = new Map<string, number>();
    dropdownData?.statuses?.forEach((s) => {
      const opt = dropdownData.allOptions?.find((o) => o.id === s.id);
      if (opt) statusOrderMap.set(s.id, opt.sort_index ?? opt.order_index ?? 0);
    });
    dropdownData?.priorities.forEach((p) => {
      const opt = dropdownData.allOptions?.find((o) => o.id === p.id);
      if (opt) priorityOrderMap.set(p.id, opt.order_index ?? 0);
    });

    if (sortStack.length > 0) {
      // A custom field rule's field, looked up once per sort rather than per
      // comparison; null for a built-in column or a field since deleted.
      const fieldOf = new Map(
        sortStack.map((rule) => {
          const id = fieldIdOfSortColumn(rule.column);
          return [rule.column, id ? (customFields.find((f) => f.id === id) ?? null) : null] as const;
        }),
      );
      // Each comparator returns "a before b" in ASCENDING terms; direction is
      // applied once, at the end, so no comparator has to know about it.
      const compareBy = (rule: SortRule, a: BacklogItem, b: BacklogItem): number => {
        switch (rule.column) {
          case "priority":
            return (
              (a.priority_id ? (priorityOrderMap.get(a.priority_id) ?? 999) : 999) -
              (b.priority_id ? (priorityOrderMap.get(b.priority_id) ?? 999) : 999)
            );
          case "created":
            return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
          case "updated":
            return new Date(a.updated_at).getTime() - new Date(b.updated_at).getTime();
          default: {
            // Reached only with both values filled: empties are settled below.
            const field = fieldOf.get(rule.column);
            return field ? compareFieldValues(field, fieldValue(a, field), fieldValue(b, field)) : 0;
          }
        }
      };
      // An empty custom field sorts last whichever way its column runs —
      // "Highest ranking first" must not open on every ticket nobody ranked —
      // so it is decided before the direction is applied, not by a comparator.
      const emptyLast = (rule: SortRule, a: BacklogItem, b: BacklogItem): number | null => {
        const field = fieldOf.get(rule.column);
        if (!field) return null;
        const aEmpty = isEmptyValue(fieldValue(a, field));
        const bEmpty = isEmptyValue(fieldValue(b, field));
        if (!aEmpty && !bEmpty) return null;
        return aEmpty === bEmpty ? 0 : aEmpty ? 1 : -1;
      };

      // Lexicographic: the first rule that separates two rows decides, and the
      // rest never run. That is what makes the stack readable as "Priority,
      // then Created within each priority band".
      filtered = [...filtered].sort((a, b) => {
        for (const rule of sortStack) {
          const empties = emptyLast(rule, a, b);
          if (empties !== null) {
            if (empties !== 0) return empties;
            continue;
          }
          const cmp = compareBy(rule, a, b);
          if (cmp !== 0) return rule.direction === "asc" ? cmp : -cmp;
        }
        return 0;
      });
    } else if (dropdownData) {
      // The implicit default: Status → Priority → Assignee (same logic as the
      // admin Resort). Nothing in the UI names it — it is simply the order an
      // unsorted backlog is in.
      filtered = [...filtered].sort((a, b) => {
        const statusA = a.status_id ? (statusOrderMap.get(a.status_id) ?? 999) : -1;
        const statusB = b.status_id ? (statusOrderMap.get(b.status_id) ?? 999) : -1;
        if (statusA !== statusB) return statusA - statusB;

        const prioA = a.priority_id ? (priorityOrderMap.get(a.priority_id) ?? 999) : 999;
        const prioB = b.priority_id ? (priorityOrderMap.get(b.priority_id) ?? 999) : 999;
        if (prioA !== prioB) return prioA - prioB;

        const assigneeA = a.assigned_to_id && a.assigned_to_id.length > 0 ? a.assigned_to_id[0] : "zzz_unassigned";
        const assigneeB = b.assigned_to_id && b.assigned_to_id.length > 0 ? b.assigned_to_id[0] : "zzz_unassigned";
        return assigneeA.localeCompare(assigneeB);
      });
    }

    return filtered;
  }, [
    items,
    searchQuery,
    selectedAssignees,
    selectedStatuses,
    selectedPriorities,
    selectedAddedBys,
    selectedTicketTypes,
    selectedPipelines,
    selectedTeams,
    dropdownData,
    subsheetScope,
    createdRange,
    updatedRange,
    fieldFilters,
    fieldSearchText,
    fieldValue,
    customFields,
    sortStack,
  ]);

  const clearFilters = () => {
    setSearchQuery("");
    setSelectedAssignees([]);
    setSelectedStatuses([]);
    setSelectedPriorities([]);
    setSelectedAddedBys([]);
    setSelectedTicketTypes([]);
    setSelectedPipelines([]);
    setSelectedTeams([]);
    setCreatedRange(EMPTY_RANGE);
    setUpdatedRange(EMPTY_RANGE);
    setFieldFilters({});
    // The sort stack deliberately survives: this button says "Clear filters",
    // and an order is not a filter. Sorts are removed from the header that set
    // them, one at a time, where the badge saying they exist also lives.
    try {
      localStorage.removeItem(FILTERS_STORAGE_KEY);
    } catch (error) {
      console.error("Failed to clear filters from localStorage:", error);
    }
  };

  const loadFilters = (filters: {
    searchQuery: string;
    selectedAssignees: string[];
    selectedStatuses: string[];
    selectedPriorities: string[];
    selectedAddedBys: string[];
    selectedTicketTypes?: string[];
    selectedPipelines?: string[];
    selectedTeams?: string[];
    customFields?: Record<string, FieldFilter>;
  }) => {
    setSearchQuery(filters.searchQuery);
    setSelectedAssignees(filters.selectedAssignees);
    setSelectedStatuses(filters.selectedStatuses);
    setSelectedPriorities(filters.selectedPriorities);
    setSelectedAddedBys(filters.selectedAddedBys);
    setSelectedTicketTypes(filters.selectedTicketTypes || []);
    setSelectedPipelines(filters.selectedPipelines || []);
    setSelectedTeams(filters.selectedTeams || []);
    setFieldFilters(parseFieldFilters(filters.customFields));
  };

  return {
    filteredItems,
    searchQuery,
    setSearchQuery,
    selectedAssignees,
    setSelectedAssignees,
    selectedStatuses,
    setSelectedStatuses,
    selectedPriorities,
    setSelectedPriorities,
    selectedAddedBys,
    setSelectedAddedBys,
    selectedTicketTypes,
    setSelectedTicketTypes,
    selectedPipelines,
    setSelectedPipelines,
    selectedTeams,
    setSelectedTeams,
    createdRange,
    setCreatedRange,
    updatedRange,
    setUpdatedRange,
    fieldFilters,
    setFieldFilter,
    sortStack,
    toggleSort,
    removeSort,
    clearSort,
    clearFilters,
    loadFilters,
  };
};
